/**
 * Alfa9 Betting Records field sources, rendered inside Jito's existing tables.
 * Casino Game Type uses the history category, which is Alfa9 presentGameRow.
 * Sports Game Type uses category, then the vendor sport, which is Alfa9 presentSportsRow.
 * Money fields stay on their own sources. A missing value stays empty.
 */

export const PAGE_SIZE = 20;
export const SPORTS_FETCH_LIMIT = 200;

export const ALFA9_RENDERED_FIELDS = Object.freeze([
  'platform',
  'category',
  'gameType',
  'gameName',
  'eventName',
  'betLabel',
  'betDetail',
  'betAmount',
  'validBet',
  'turnover',
  'profitLoss',
  'winAmount',
  'lossAmount',
  'netAmount',
  'result',
  'settlementStatus',
  'status',
  'referenceId',
]);

export function textOrEmpty(value) {
  if (value == null) return '';
  const text = String(value).trim();
  if (!text || text === '-' || text === '—') return '';
  return text;
}

export function providerOrEmpty(value) {
  const text = textOrEmpty(value);
  const key = text.toLowerCase();
  if (key === 'igaming' || key === 'igaming api') return '';
  return text;
}

export function numberOrEmpty(value) {
  if (value == null || value === '' || value === '-' || value === '—') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

export function gameNameOrEmpty(row) {
  const name = textOrEmpty(row && row.gameName);
  const code = textOrEmpty(row && row.gameCode);
  if (!name) return '';
  if (code && name === code) return '';
  if (/^\d+$/.test(name)) return '';
  return name;
}

export function sportsFieldValues(row) {
  const validBet = numberOrEmpty(row && row.validBet);
  const winLoss = numberOrEmpty(row && row.winLossAmount);
  return {
    platform: providerOrEmpty(row && row.provider),
    category: textOrEmpty(row && row.category),
    gameType: textOrEmpty(row && row.category) || textOrEmpty(row && row.gameType),
    gameName: gameNameOrEmpty(row),
    eventName: textOrEmpty(row && row.eventName),
    betLabel: textOrEmpty(row && row.betLabel),
    betDetail: textOrEmpty(row && row.betDetail),
    betAmount: numberOrEmpty(row && row.betAmount),
    validBet,
    turnover: validBet,
    profitLoss: winLoss,
    winAmount: null,
    lossAmount: null,
    netAmount: null,
    result: textOrEmpty(row && row.result),
    settlementStatus: textOrEmpty(row && row.settlementStatus),
    status: textOrEmpty(row && row.settlementStatus),
    referenceId: textOrEmpty(row && row.id),
  };
}

export function gameFieldValues(row) {
  const betAmount = numberOrEmpty(row && row.betAmount);
  const netAmount = numberOrEmpty(row && row.netAmount);
  return {
    platform: providerOrEmpty(row && row.provider),
    category: textOrEmpty(row && row.category),
    gameType: textOrEmpty(row && row.category),
    gameName: gameNameOrEmpty(row),
    eventName: '',
    betLabel: textOrEmpty(row && row.betLabel),
    betDetail: textOrEmpty(row && row.betDetail),
    betAmount,
    validBet: numberOrEmpty(row && row.validBet),
    turnover: betAmount,
    profitLoss: netAmount,
    winAmount: numberOrEmpty(row && row.winAmount),
    lossAmount: numberOrEmpty(row && row.lossAmount),
    netAmount,
    result: textOrEmpty(row && row.result),
    settlementStatus: textOrEmpty(row && row.settlementStatus),
    status: textOrEmpty(row && row.status),
    referenceId: textOrEmpty(row && row.referenceId),
  };
}

export function sportsHistoryQuery(state) {
  const query = {
    page: 1,
    limit: SPORTS_FETCH_LIMIT,
    gameType: state.gameType,
    result: state.result,
    search: state.search,
    from: state.from,
    to: state.to,
  };
  if (state.recordState === 'Settled') query.status = 'settled';
  else if (state.recordState === 'Unsettled') query.status = 'open';
  return query;
}

export function gameHistoryQuery(state) {
  const query = { ...state };
  delete query.recordState;
  if (state.recordState === 'Unsettled') {
    query.type = 'Game';
    query.result = 'BET';
    query.page = 1;
    query.limit = SPORTS_FETCH_LIMIT;
  } else if (state.recordState === 'Settled') {
    query.type = 'Game';
    query.result = 'All';
    query.page = 1;
    query.limit = SPORTS_FETCH_LIMIT;
  }
  return query;
}

function sameText(left, right) {
  return textOrEmpty(left).toLowerCase() === textOrEmpty(right).toLowerCase();
}

export function filterSportsRows(records, state) {
  return (records || []).filter((row) => {
    if (state.provider && state.provider !== 'All' && !sameText(row.provider, state.provider)) return false;
    const settlement = textOrEmpty(row.settlementStatus).toLowerCase();
    if (state.recordState === 'Settled') return settlement === 'settled';
    if (state.recordState === 'Unsettled') return settlement === 'open';
    return true;
  });
}

export function isUnsettledGameRow(row) {
  if (textOrEmpty(row && row.result).toUpperCase() !== 'BET') return false;
  const win = numberOrEmpty(row && row.winAmount);
  const net = numberOrEmpty(row && row.netAmount);
  const loss = numberOrEmpty(row && row.lossAmount);
  if (win && win > 0) return false;
  if (net && net !== 0) return false;
  if (loss && loss > 0) return false;
  return true;
}

export function filterGameRows(records, state) {
  return (records || []).filter((row) => {
    if (state.recordState === 'Settled') {
      const result = textOrEmpty(row.result).toUpperCase();
      return result === 'WIN' || result === 'LOSS' || result === 'REFUND';
    }
    if (state.recordState === 'Unsettled') return isUnsettledGameRow(row);
    return true;
  });
}

function slicePage(filtered, state) {
  const page = Math.max(1, Number(state.page) || 1);
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  return {
    rows: filtered.slice(start, start + PAGE_SIZE),
    total,
    totalPages,
    page: safePage,
  };
}

export function pageSportsRows(records, state) {
  return slicePage(filterSportsRows(records, state), state);
}

export function pageGameRows(records, state, serverPage) {
  if (!state || !state.recordState || state.recordState === 'All') {
    return {
      rows: records || [],
      total: Number(serverPage && serverPage.total) || (records || []).length,
      totalPages: Number(serverPage && serverPage.totalPages) || 1,
      page: Number(serverPage && serverPage.page) || Number(state && state.page) || 1,
    };
  }
  return slicePage(filterGameRows(records, state), state);
}

export function providerChoices(records) {
  const seen = new Set();
  const choices = [];
  (records || []).forEach((row) => {
    const value = textOrEmpty(row && row.provider);
    const key = value.toLowerCase();
    if (!value || seen.has(key)) return;
    seen.add(key);
    choices.push({ value, label: value });
  });
  return choices;
}
